import { $, $$, on, doc, body } from './util.js';
import { readSettings, writeSettings, SETTINGS_KEY } from './settings.js';

/* 1. THEME MENU
   --------------------------------------------------------------------------
   Three modes behind the nav's theme button: light, dark and system. Light and
   dark are the user pinning a theme; system resolves off the **clock**, not off
   prefers-color-scheme — DAY_START..DAY_END local time is light, the rest dark.

   The button carries two gestures, the way GitHub's does: **hover opens the
   menu, click steps to the next mode**. Neither is the only way in — keyboard
   (Enter/Space, or ArrowDown) and touch both open the menu instead of cycling,
   since they have no hover to open it with. See the click handler.

   Two attributes carry the state, both on <html>:
     data-theme       the resolved theme (light|dark) — drives color-scheme
     data-theme-mode  the chosen mode (light|dark|system) — drives which glyph
                      the button shows and which row in the menu is ticked

   Both are written before first paint by the inline script in
   includes/frontend/base-template.html, which duplicates the resolve rule
   below. THE TWO MUST AGREE: change DAY_START/DAY_END here and the literals
   there have to change with them, or the pre-paint theme and the one this
   module settles on will disagree for a frame. */

/* The chosen mode is the `theme` property of the shared settings entry
   (settings.js), which also moved it there from the old 'cd-theme' key. */
var MODES = ['light', 'dark', 'system'];   // also the click-cycle order
var DAY_START = 8;   // 08:00 — light from here…
var DAY_END = 18;    // …until 18:00; dark outside the window.
var HOVER_OPEN_DELAY = 140;   // ms of dwell before hover opens the menu
var HOVER_CLOSE_DELAY = 260;  // ms of grace after the pointer leaves

/* Which theme "system" means right now. */
function clockTheme() {
	var h = new Date().getHours();
	return (h >= DAY_START && h < DAY_END) ? 'light' : 'dark';
}

/* Anything that is not an explicit light/dark choice — including no stored
   choice at all — is system, so that is also the default on a first visit.
   Older builds only ever stored 'light'/'dark', so a returning visitor keeps
   whatever they had picked. */
function readMode() {
	var saved = readSettings().theme;
	return MODES.indexOf(saved) === -1 ? 'system' : saved;
}

function resolve(mode) {
	return mode === 'system' ? clockTheme() : mode;
}

/* The next moment at which clockTheme() changes its answer: today's 08:00,
   today's 18:00, or tomorrow's 08:00, whichever comes first. */
function nextBoundary() {
	var now = new Date();
	var h = now.getHours();
	var at = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
	if (h < DAY_START) { at.setHours(DAY_START); }
	else if (h < DAY_END) { at.setHours(DAY_END); }
	else { at.setDate(at.getDate() + 1); at.setHours(DAY_START); }
	// Never schedule a zero or negative delay — a DST shift can put the boundary
	// behind us, and a 0ms timer that re-arms itself is a busy loop.
	return Math.max(1000, at.getTime() - now.getTime());
}

export function initTheme() {
	var btn = $('#themeToggle');
	var menu = $('#themeMenu');
	var wrap = btn ? (btn.closest ? btn.closest('.nav-theme-wrap') : btn.parentNode) : null;
	var opts = menu ? $$('[data-theme-choice]', menu) : [];
	var mode = readMode();
	var closeTimer = null;
	// Two hover timers, not one: a single variable cannot hold both a pending
	// open and a pending close, and re-entering during the close grace period
	// has to cancel the close while the menu is already open.
	var hoverOpenTimer = null;
	var hoverCloseTimer = null;
	var tickTimer = null;
	// Whether this pointer can hover at all decides which gesture gets the menu;
	// see the click handler. Read once — a device does not grow a mouse.
	var canHover = !!(wrap && window.matchMedia && window.matchMedia('(hover: hover)').matches);

	function persist() {
		writeSettings({ theme: mode });
	}

	function apply(next) {
		mode = next;
		var theme = resolve(mode);
		doc.documentElement.setAttribute('data-theme', theme);
		doc.documentElement.setAttribute('data-theme-mode', mode);
		body.classList.toggle('dark', theme === 'dark');
		sync();
	}

	/* aria-checked and the button's tooltip are the parts a stylesheet cannot
	   do; the tick itself and the button glyph are pure CSS off
	   :root[data-theme-mode] (see scss/components/_nav.scss), so those are
	   already correct before this module runs. */
	function sync() {
		var current = null;
		opts.forEach(function (opt) {
			var chosen = opt.getAttribute('data-theme-choice') === mode;
			if (chosen) { current = opt; }
			opt.setAttribute('aria-checked', chosen ? 'true' : 'false');
			opt.classList.toggle('selected', chosen);
		});
		if (!btn) { return; }
		// The base label ("Theme") is authored in data/site.html and parked on
		// the button, so the copy stays in one place.
		var label = btn.getAttribute('data-label') || 'Theme';
		var name = current ? $('.theme-opt-name', current) : null;
		if (name) { label += ': ' + name.textContent; }
		btn.setAttribute('title', label);
		btn.setAttribute('aria-label', label);
	}

	/* While the mode is system the theme has to change on its own at 08:00 and
	   18:00 — a tab left open overnight would otherwise stay light until it is
	   reloaded. One timer aimed at the next boundary, re-armed each time it
	   fires; no polling. */
	function schedule() {
		if (tickTimer) { clearTimeout(tickTimer); tickTimer = null; }
		if (mode !== 'system') { return; }
		tickTimer = setTimeout(function () { apply('system'); schedule(); }, nextBoundary());
	}

	apply(mode);
	schedule();

	// No menu markup — an older cached template, say. Fall back to the plain
	// two-state toggle this button used to be, so the control is never inert.
	if (!btn || !menu) {
		on(btn, 'click', function () {
			apply(resolve(mode) === 'dark' ? 'light' : 'dark');
			persist();
			schedule();
		});
		return;
	}

	function isOpen() {
		return btn.getAttribute('aria-expanded') === 'true';
	}

	function cancelHoverTimers() {
		if (hoverOpenTimer) { clearTimeout(hoverOpenTimer); hoverOpenTimer = null; }
		if (hoverCloseTimer) { clearTimeout(hoverCloseTimer); hoverCloseTimer = null; }
	}

	function open() {
		if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
		cancelHoverTimers();
		menu.hidden = false;
		btn.setAttribute('aria-expanded', 'true');
		// Next frame, so there is a painted starting state to animate from.
		requestAnimationFrame(function () { menu.classList.add('open'); });
	}

	function close(refocus) {
		cancelHoverTimers();
		btn.setAttribute('aria-expanded', 'false');
		menu.classList.remove('open');
		closeTimer = setTimeout(function () { menu.hidden = true; closeTimer = null; }, 200);
		if (refocus) { btn.focus(); }
	}

	function focusOpt(i) {
		if (!opts.length) { return; }
		var n = opts.length;
		opts[((i % n) + n) % n].focus();
	}

	/* Click advances one step through MODES rather than opening the menu — the
	   menu is what hover is for. Order matches the menu top to bottom, so the
	   two gestures agree about what "next" means. */
	function cycle() {
		var i = MODES.indexOf(mode);
		apply(MODES[(i + 1) % MODES.length]);
		persist();
		schedule();
	}

	/* Two gestures on one button, so a click has to be read carefully:

	   - A keyboard Enter/Space arrives as a click with detail === 0. Those open
	     the menu: aria-haspopup promises a menu, and a keyboard user has no
	     hover to open it with.
	   - On a device with no hover at all (touch), a tap also opens the menu, for
	     the same reason — cycling there would leave the menu unreachable and the
	     third mode undiscoverable.
	   - Everything else — a real mouse click — cycles.

	   Cycling with the menu already open is deliberate: the tick moves under the
	   pointer, which is the clearest possible feedback for what the click did. */
	function isPointerClick(e) {
		return canHover && e.detail > 0;
	}

	on(btn, 'click', function (e) {
		e.preventDefault();
		if (isPointerClick(e)) { cycle(); return; }
		isOpen() ? close(false) : open();
	});

	/* Hover opens the menu, on both the button and the panel — the panel is a
	   descendant of the wrapper, so moving into it does not count as leaving.
	   The delays are intent filters: a pointer crossing the button on its way
	   somewhere else should not fling a menu open, and the gap between button
	   and panel (plus the bridge in _nav.scss) should not slam it shut. */
	if (canHover) {
		on(wrap, 'mouseenter', function () {
			if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
			// Coming back during the grace period cancels the close — including
			// when the menu is still open, which is the common case.
			cancelHoverTimers();
			if (isOpen()) { return; }
			hoverOpenTimer = setTimeout(function () { hoverOpenTimer = null; open(); }, HOVER_OPEN_DELAY);
		});
		on(wrap, 'mouseleave', function () {
			cancelHoverTimers();
			if (!isOpen()) { return; }
			// Keep it open while the keyboard is in it — closing under someone's
			// focus would drop them back to the top of the page.
			if (wrap.contains(doc.activeElement)) { return; }
			hoverCloseTimer = setTimeout(function () { hoverCloseTimer = null; close(false); }, HOVER_CLOSE_DELAY);
		});
	}

	on(btn, 'keydown', function (e) {
		if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') { return; }
		e.preventDefault();
		if (!isOpen()) { open(); }
		// The menu is displayed but not yet painted at this point; focusing on
		// the next frame keeps the page from jumping.
		requestAnimationFrame(function () { focusOpt(e.key === 'ArrowDown' ? 0 : opts.length - 1); });
	});

	opts.forEach(function (opt, i) {
		on(opt, 'click', function () {
			apply(opt.getAttribute('data-theme-choice'));
			persist();
			schedule();
			close(true);
		});
		on(opt, 'keydown', function (e) {
			if (e.key === 'ArrowDown') { e.preventDefault(); focusOpt(i + 1); }
			else if (e.key === 'ArrowUp') { e.preventDefault(); focusOpt(i - 1); }
			else if (e.key === 'Home') { e.preventDefault(); focusOpt(0); }
			else if (e.key === 'End') { e.preventDefault(); focusOpt(opts.length - 1); }
		});
	});

	on(doc, 'keydown', function (e) {
		if (e.key === 'Escape' && isOpen()) { close(true); }
	});

	on(doc, 'click', function (e) {
		if (!isOpen()) { return; }
		if (menu.contains(e.target) || btn.contains(e.target)) { return; }
		close(false);
	});

	// Tab out of the menu and it has done its job. focusout fires before the
	// next element takes focus, so read activeElement a tick later.
	on(menu, 'focusout', function () {
		setTimeout(function () {
			if (!isOpen()) { return; }
			if (menu.contains(doc.activeElement) || doc.activeElement === btn) { return; }
			close(false);
		}, 0);
	});

	// A machine asleep across 18:00 wakes with a stale theme and a timer that
	// never fired; re-resolve and re-arm when the tab is looked at again.
	on(doc, 'visibilitychange', function () {
		if (doc.hidden || mode !== 'system') { return; }
		apply('system');
		schedule();
	});

	// Another tab changed the preference — follow it rather than disagreeing.
	on(window, 'storage', function (e) {
		if (e.key && e.key !== SETTINGS_KEY) { return; }
		apply(readMode());
		schedule();
	});
}
